<template>
  <div class="w-full h-full p-4">
    <div class="flex gap-6 flex-wrap">
      <div class="relative" :style="{ width: frameW + 'px', height: frameH + 'px' }">
        <video ref="videoEl"
               :width="frameW" :height="frameH"
               class="rounded-xl shadow bg-black block"
               playsinline muted></video>

        <canvas ref="canvasEl"
                class="absolute left-0 top-0 rounded-xl pointer-events-none"
                :width="frameW" :height="frameH"
                :style="{ width: frameW + 'px', height: frameH + 'px' }"></canvas>

        <!-- 좌측 하단 상태 -->
        <div class="absolute left-2 bottom-2 bg-white/80 text-black text-sm rounded px-2 py-1 space-y-0.5 shadow">
          <div>Index tip (px): {{ indexTip.x.toFixed(1) }}, {{ indexTip.y.toFixed(1) }}</div>
          <div>Markers: {{ detectedMarkers.length }}</div>
          <div>{{ status }}</div>
        </div>
      </div>

      <div class="min-w-[320px] max-w-[420px]">
        <h2 class="text-lg font-semibold mb-2">Controls</h2>
        <ul class="list-disc pl-5 text-sm leading-6 mb-3">
          <li>카메라 허용 후 자동 시작</li>
          <li><kbd class="px-1 py-0.5 bg-gray-200 rounded">ArrowDown</kbd> = 스냅샷에 좌표 찍어 다운로드</li>
          <li>마커 ≥ 1이면 BBox 스냅샷 갱신(손 없음일 때만)</li>
        </ul>

        <!-- 카메라 선택 -->
        <label class="text-sm font-medium">카메라 선택</label>
        <div class="flex gap-2 items-center mb-3">
          <select v-model="selectedDeviceId" class="border rounded px-2 py-1 w-full">
            <option v-for="d in devices" :key="d.deviceId" :value="d.deviceId">{{ d.label || 'Camera' }}</option>
          </select>
          <button class="px-3 py-2 bg-gray-100 rounded" @click="restartWithDevice" :disabled="!selectedDeviceId">변경</button>
        </div>

        <div class="flex flex-wrap gap-2 mb-2">
          <button class="px-3 py-2 bg-blue-600 text-white rounded-lg shadow hover:bg-blue-700" @click="savePointOnSnapshot" :disabled="!streamReady">ROI 저장</button>
          <button class="px-3 py-2 bg-gray-200 rounded" @click="downloadLastFrame" :disabled="!streamReady">현재 프레임 다운로드</button>
        </div>

        <!-- <div class="flex items-center gap-3 mb-2 text-sm">
          <label class="inline-flex items-center gap-2">
            <input type="checkbox" v-model="drawBBox"> BBox 표시(디버그)
          </label>
          <label class="inline-flex items-center gap-2">
            패딩(px)
            <input type="number" class="border rounded px-2 py-1 w-20" v-model.number="bboxPadPx" min="0" />
          </label>
          <label class="inline-flex items-center gap-2">
            최소넓이(px²)
            <input type="number" class="border rounded px-2 py-1 w-24" v-model.number="minBBoxArea" min="1" />
          </label>
        </div> -->

        <!-- Controls 밑, 아무 데서나 보기 편한 곳에 추가 -->
        <div v-if="previewUrl" class="mt-3 p-3 border rounded bg-white shadow">
          <div class="text-sm mb-2">🖼️ 합성된 ROI 미리보기</div>
          <img :src="previewUrl" alt="ROI preview" class="max-w-full rounded border" />
          <div class="flex gap-2 mt-2">
            <button class="px-3 py-2 bg-gray-200 rounded" @click="clearPreview">지우기</button>
            <button class="px-3 py-2 bg-blue-600 text-white rounded" @click="downloadPreview" :disabled="!previewUrl">다운로드</button>
          </div>
        </div>


        <p class="text-xs text-gray-500 mt-3">
          * js-aruco(MIP_36h12, worker) 사용. 마커 코너의 최소 BBox로 촉각패드 영역을 스냅샷 저장(손이 프레임에 없을 때). ↓ 또는 버튼으로 스냅샷 위에 검지 좌표를 합성해 PNG 다운로드.
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import { markRaw } from 'vue'

let ApiService
try {
  ApiService = (await import('@/services/ApiService.js')).default
} catch (_) {}

/* ---- 소도구 ---- */
function centroid(cs){ return { x:(cs[0].x+cs[1].x+cs[2].x+cs[3].x)/4, y:(cs[0].y+cs[1].y+cs[2].y+cs[3].y)/4 } }

export default {
  name: 'webcamArucoBBoxSnapshot',
  data() {
    return {
      status: 'Initializing...',
      frameW: 640, frameH: 480,
      indexTip: { x: 0, y: 0 },

      _hands: null,
      _stream: null,
      streamReady: false,

      devices: [], selectedDeviceId: null,
      previewUrl: null,       // ← 합성 이미지 미리보기 dataURL
      _lastOutCanvas: null,   // ← 필요 시 재다운로드용 내부 보관(선택)

      // ArUco (worker)
      worker: null, workerReady: false,
      useBitmap: !!self.createImageBitmap,
      detectedMarkers: [],
      procW: 800, procH: 600,
      detectHz: 30,
      _lastDetectTs: 0,
      _downCanvas: null, _downCtx: null,

      // BBox 스냅샷
      handPresent: false,
      drawBBox: true,
      bboxPadPx: 10,         // BBox 외곽 패딩
      minBBoxArea: 15000,    // 너무 작은 BBox는 무시
      cleanRegionCanvas: null,
      cleanRegionRect: null, // {x,y,w,h}
    }
  },
  mounted() {
    this.init()
    window.addEventListener('keydown', this.onKeyDown)
    window.addEventListener('resize', this.syncSizes)
  },
  beforeUnmount() {
    window.removeEventListener('keydown', this.onKeyDown)
    window.removeEventListener('resize', this.syncSizes)
    this.stopStream()
    this.safeCloseHands()
    if (this.worker) { this.worker.terminate(); this.worker = null }
    this.clearPreview()
  },
  methods: {
    /* ---------- Sizes ---------- */
    syncSizes() {
      const v = this.$refs.videoEl
      if (!v || !v.videoWidth) return
      this.frameW = v.videoWidth
      this.frameH = v.videoHeight
      const c = this.$refs.canvasEl
      if (c) { c.width = this.frameW; c.height = this.frameH }
    },

    /* ---------- Init ---------- */
    async init() {
      try {
        await this.loadHandsScripts()
        await this.primePermission()
        await this.refreshDevices()
        await this.startCamera()
        await this.setupHands()
        await this.initArucoWorker()

        this.status = 'Running (worker mode)'
        await this.$nextTick()
        this.requestVideoFrame()
      } catch (e) {
        console.error(e)
        this.status = 'Init error: ' + (e?.message || e)
      }
    },

    /* ---------- Worker ---------- */
    async initArucoWorker() {
      this.worker = new Worker(new URL('@/js/arucoWorker.js', import.meta.url), { type: 'classic' })
      this.worker.onmessage = (ev) => {
        const { type } = ev.data || {}
        if (type === 'ready') {
          this.workerReady = true
        } else if (type === 'result') {
          this.detectedMarkers = Array.isArray(ev.data.markers) ? ev.data.markers : []
        } else if (type === 'error') {
          console.warn('[ArucoWorker]', ev.data.error)
        }
      }
      this.worker.postMessage({ type: 'init', dicName: 'ARUCO_MIP_36h12', procW: this.procW, procH: this.procH })
    },

    async pushFrameToWorker() {
      if (!this.worker || !this.workerReady) return
      const now = performance.now()
      if (now - this._lastDetectTs < (1000 / this.detectHz)) return
      this._lastDetectTs = now

      const v = this.$refs.videoEl
      if (this.useBitmap && window.createImageBitmap) {
        try {
          const bmp = await createImageBitmap(v) // 원본 → 워커에서 다운스케일
          this.worker.postMessage(
            { type: 'frame', bitmap: bmp, frameW: this.frameW, frameH: this.frameH, procW: this.procW, procH: this.procH, ts: now },
            [bmp]
          )
          return
        } catch (e) { console.warn('ImageBitmap failed, fallback RGBA', e) }
      }
      this.sendRGBAFallback(now)
    },

    requestVideoFrame() {
      const v = this.$refs.videoEl
      if (!v || !this.streamReady) {
        setTimeout(() => this.requestVideoFrame(), 60)
        return
      }
      v.requestVideoFrameCallback(async () => {
        await this.loopOnce()
        this.requestVideoFrame()
      })
    },

    // 마커들의 모든 코너 → 최소 BBox(+padding). 손이 없고 면적이 충분하면 스냅샷 갱신
    maybeUpdateSnapshotFromMarkers(markers) {
      if (!markers?.length || this.handPresent) return

      const xs = [], ys = []
      for (const m of markers) {
        if (!m?.corners) continue
        for (const c of m.corners) { xs.push(c.x); ys.push(c.y) }
      }
      if (!xs.length) return

      let minX = Math.max(0, Math.floor(Math.min(...xs) - this.bboxPadPx))
      let minY = Math.max(0, Math.floor(Math.min(...ys) - this.bboxPadPx))
      let maxX = Math.min(this.frameW, Math.ceil(Math.max(...xs) + this.bboxPadPx))
      let maxY = Math.min(this.frameH, Math.ceil(Math.max(...ys) + this.bboxPadPx))

      const w = Math.max(1, maxX - minX)
      const h = Math.max(1, maxY - minY)
      if (w * h < this.minBBoxArea) return // 너무 작으면 무시

      // 비디오에서 해당 영역만 복사
      const v = this.$refs.videoEl
      const off = this.cleanRegionCanvas || document.createElement('canvas')
      off.width = w; off.height = h
      const octx = off.getContext('2d')
      octx.imageSmoothingEnabled = false
      octx.drawImage(v, minX, minY, w, h, 0, 0, w, h)

      this.cleanRegionCanvas = off
      this.cleanRegionRect = { x: minX, y: minY, w, h }
      this.status = `Snapshot updated (BBox ${w}×${h})`
    },

    // ↓ / 버튼: 마지막 스냅샷에 현재 검지 좌표를 찍어 다운로드
    savePointOnSnapshot() {
      // 1) 현재 검지 끝 좌표 (원본 프레임 좌표계)
      const px = this.indexTip.x
      const py = this.indexTip.y
      this.status = `IndexTip px: (${px.toFixed(1)}, ${py.toFixed(1)})`

      // 2) 스냅샷 준비 확인
      if (!this.cleanRegionCanvas || !this.cleanRegionRect) {
        console.warn('No clean snapshot yet.')
        this.status = 'No clean snapshot yet'
        return
      }

      const { x, y, w, h } = this.cleanRegionRect
      // ROI 좌표계로 변환
      const localX = px - x
      const localY = py - y

      // 3) 출력 캔버스
      const out = document.createElement('canvas')
      out.width = w
      out.height = h
      const ctx = out.getContext('2d')
      ctx.imageSmoothingEnabled = false

      // 4) 180° 회전: 이미지와 좌표를 동일한 변환 아래에서 그린다
      ctx.save()
      ctx.translate(w, h)
      ctx.rotate(Math.PI)

      // 스냅샷 복사 (회전된 좌표계에서 0,0 에서 시작)
      ctx.drawImage(this.cleanRegionCanvas, 0, 0)

      // 5) 점도 같은 변환 아래에서 '원래 로컬 좌표'로 그린다 (추가 보정 불필요!)
      ctx.beginPath()
      const r = Math.max(4, Math.round(Math.min(w, h) * 0.015))
      ctx.arc(localX, localY, r, 0, Math.PI * 2)
      ctx.fillStyle = 'red'
      ctx.fill()               // ⬅️ 테두리(stroke) 없음
      ctx.restore()

      // 6) 저장
      this.downloadCanvas(out, `roi_point_${Date.now()}.png`)
      this.status = 'Saved rotated snapshot with point'
    },

    async loopOnce() {
      const canvas = this.$refs.canvasEl
      const ctx = canvas?.getContext('2d')
      if (!ctx) return

      this.syncSizes()

      // 1) MediaPipe Hands → 검지 좌표/손 유무 갱신
      if (this._hands) {
        try { await this._hands.send({ image: this.$refs.videoEl }) } catch {}
      }

      // 2) ArUco (워커로 프레임 전송 → onmessage에서 detectedMarkers 갱신)
      await this.pushFrameToWorker()
      const markers = this.detectedMarkers || []

      // 3) 손이 없으면 BBox 스냅샷 갱신
      this.maybeUpdateSnapshotFromMarkers(markers)

      // 4) 디버그 표시(마커/ID/BBox)
      ctx.clearRect(0, 0, this.frameW, this.frameH)
      if (markers.length) {
        ctx.save()
        ctx.lineWidth = 2
        ctx.strokeStyle = '#FF3B30'
        for (const m of markers) {
          ctx.beginPath()
          m.corners.forEach((c,i)=> i?ctx.lineTo(c.x,c.y):ctx.moveTo(c.x,c.y))
          ctx.closePath(); ctx.stroke()
          const ctr = centroid(m.corners)
          ctx.fillStyle = '#FF3B30'; ctx.font = 'bold 14px sans-serif'
          ctx.fillText(`ID:${m.id}`, ctr.x+6, ctr.y-6)
        }
        if (this.drawBBox && this.cleanRegionRect) {
          const { x,y,w,h } = this.cleanRegionRect
          ctx.strokeStyle = 'red'; ctx.lineWidth = 2
          ctx.strokeRect(x, y, w, h)
        }
        ctx.restore()
      }
    },

    /* ---------- RGBA fallback ---------- */
    sendRGBAFallback(ts) {
      if (!this._downCanvas) {
        this._downCanvas = document.createElement('canvas')
        this._downCanvas.width = this.procW
        this._downCanvas.height = this.procH
        this._downCtx = this._downCanvas.getContext('2d', { willReadFrequently: true })
      }
      this._downCtx.imageSmoothingEnabled = false
      this._downCtx.drawImage(this.$refs.videoEl, 0, 0, this.procW, this.procH)
      const imageData = this._downCtx.getImageData(0, 0, this.procW, this.procH)
      const buf = imageData.data.buffer.slice(0)
      this.worker.postMessage({
        type: 'frame',
        rgba: buf,
        frameW: this.frameW, frameH: this.frameH,
        procW: this.procW, procH: this.procH,
        ts
      }, [buf])
    },

    /* ---------- Camera / Hands ---------- */
    async loadHandsScripts() {
      if (window.Hands && window.drawConnectors && window.drawLandmarks) return
      await this.injectScript('https://cdn.jsdelivr.net/npm/@mediapipe/drawing_utils')
      await this.injectScript('https://cdn.jsdelivr.net/npm/@mediapipe/hands')
    },
    injectScript(src) {
      return new Promise((resolve, reject) => {
        if (document.querySelector(`script[src="${src}"]`)) return resolve()
        const s = document.createElement('script')
        s.src = src; s.async = true
        s.onload = () => resolve()
        s.onerror = () => reject(new Error('Failed to load: ' + src))
        document.head.appendChild(s)
      })
    },
    async primePermission() {
      try {
        const tmp = await navigator.mediaDevices.getUserMedia({ video: true, audio: false })
        tmp.getTracks().forEach(t => t.stop())
      } catch {}
    },
    async refreshDevices() {
      const list = await navigator.mediaDevices.enumerateDevices()
      this.devices = list.filter(d => d.kind === 'videoinput')
      if (!this.selectedDeviceId && this.devices.length) this.selectedDeviceId = this.devices[0].deviceId
    },
    stopStream() {
      if (this._stream) this._stream.getTracks().forEach(t => t.stop())
      this._stream = null
      this.streamReady = false
    },
    async startCamera() {
      this.stopStream()
      const video = this.$refs.videoEl
      const constraints = this.selectedDeviceId
        ? { video: { deviceId: { exact: this.selectedDeviceId }, width: { ideal: 1280 }, height: { ideal: 720 } }, audio: false }
        : { video: { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 720 } }, audio: false }
      try {
        this._stream = await navigator.mediaDevices.getUserMedia(constraints)
      } catch {
        this._stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false })
      }
      video.srcObject = this._stream

      await new Promise(res => { const h = () => { video.removeEventListener('loadedmetadata', h); res() }; video.addEventListener('loadedmetadata', h) })
      await video.play()
      this.streamReady = true
      this.syncSizes()
      this.status = `Camera ready (${this.frameW}×${this.frameH})`
    },
    async restartWithDevice() {
      try { await this.startCamera() } catch (e) { console.error(e); this.status = 'Camera switch error: ' + (e?.name || e?.message || e) }
    },
    async setupHands() {
      const canvas = this.$refs.canvasEl
      const ctx = canvas?.getContext('2d')
      if (!ctx) return

      const hands = new window.Hands({
        locateFile: (file) => `https://cdn.jsdelivr.net/npm/@mediapipe/hands/${file}`,
      })
      hands.setOptions({
        maxNumHands: 1, modelComplexity: 0,
        minDetectionConfidence: 0.6, minTrackingConfidence: 0.6,
        selfieMode: false,
      })

      hands.onResults((results) => {
        if (!this.streamReady) return
        this.syncSizes()

        const present = !!(results.multiHandLandmarks && results.multiHandLandmarks.length)
        this.handPresent = present
        const ctx = this.$refs.canvasEl.getContext('2d')
        if (!ctx) return

        if (!present) return
        const lm = results.multiHandLandmarks[0]
        const tip = lm[8]
        const x = tip.x * this.frameW, y = tip.y * this.frameH
        this.indexTip = { x, y }

        // 손가락 표시만(디버그)
        ctx.save()
        ctx.beginPath()
        ctx.arc(x, y, 6, 0, Math.PI*2)
        ctx.fillStyle = 'rgba(0, 200, 255, 0.9)'
        ctx.fill()
        ctx.restore()
      })

      this._hands = markRaw(hands)
    },

    /* ---------- Download helpers ---------- */
    async getFrameDataURL() {
      const v = this.$refs.videoEl
      const w = v.videoWidth, h = v.videoHeight
      const cvs = document.createElement('canvas')
      cvs.width = w; cvs.height = h
      cvs.getContext('2d').drawImage(v, 0, 0, w, h)
      return cvs.toDataURL('image/png')
    },
    async getOverlayBlob() {
      const dataUrl = await this.getFrameDataURL()
      const res = await fetch(dataUrl)
      return await res.blob()
    },
    downloadBlob(blob, filename) {
      const a = document.createElement('a')
      a.href = URL.createObjectURL(blob)
      a.download = filename
      a.click()
      URL.revokeObjectURL(a.href)
    },
    async downloadLastFrame() {
      const blob = await this.getOverlayBlob()
      this.downloadBlob(blob, `frame_${Date.now()}.png`)
    },
    downloadCanvas(canvas, filename = 'snapshot.png') {
      try {
        const a = document.createElement('a')
        a.href = canvas.toDataURL('image/png')
        a.download = filename
        a.click()
        setTimeout(() => URL.revokeObjectURL?.(a.href), 0)
      } catch (e) {
        console.warn('download fallback', e)
        window.open(canvas.toDataURL('image/png'), '_blank')
      }
    },

    /* ---------- Key ---------- */
    onKeyDown(e) {
      if (e.key !== 'ArrowDown') return
      e.preventDefault()
      if (e.ctrlKey) {
        // 기존 동작: 즉시 다운로드
        this.savePointOnSnapshot(true)
      } else {
        // 미리보기만
        this.savePointOnSnapshot(false)
      }
    },

    /* ---------- Cleanup ---------- */
    async safeCloseHands() {
      try { if (this._hands?.close) await this._hands.close() } catch {} finally { this._hands = null }
    },

    // ↓ / 버튼 공용: 합성 이미지 만들고, 미리보기/다운로드 선택
    savePointOnSnapshot(shouldDownload = false) {
      // 1) 현재 검지 끝 좌표 (원본 프레임 좌표계)
      const px = this.indexTip.x
      const py = this.indexTip.y
      this.status = `IndexTip px: (${px.toFixed(1)}, ${py.toFixed(1)})`

      // 2) 스냅샷 준비 확인
      if (!this.cleanRegionCanvas || !this.cleanRegionRect) {
        console.warn('No clean snapshot yet.')
        this.status = 'No clean snapshot yet'
        return
      }

      const { x, y, w, h } = this.cleanRegionRect
      // ROI 좌표계로 변환
      const localX = px - x
      const localY = py - y

      // 3) 출력 캔버스
      const out = document.createElement('canvas')
      out.width = w
      out.height = h
      const ctx = out.getContext('2d')
      ctx.imageSmoothingEnabled = false

      // 4) 180° 회전: 이미지와 좌표를 동일한 변환 아래에서 그린다
      ctx.save()
      ctx.translate(w, h)
      ctx.rotate(Math.PI)

      // 스냅샷 복사 (회전된 좌표계에서 0,0 에서 시작)
      ctx.drawImage(this.cleanRegionCanvas, 0, 0)

      // 5) 점도 같은 변환 아래에서 '원래 로컬 좌표'로 그린다
      ctx.beginPath()
      const r = Math.max(4, Math.round(Math.min(w, h) * 0.015))
      ctx.arc(localX, localY, r, 0, Math.PI * 2)
      ctx.fillStyle = 'red'
      ctx.fill()
      ctx.restore()

      // 6) 미리보기/다운로드 처리
      const dataUrl = out.toDataURL('image/png')
      this._lastOutCanvas = out
      this.previewUrl = dataUrl
      this.status = shouldDownload
        ? 'Saved rotated snapshot with point'
        : 'Preview updated (rotated)'

      if (shouldDownload) {
        this.downloadCanvas(out, `roi_point_${Date.now()}.png`)
      }
    },

    downloadPreview() {
      if (!this._lastOutCanvas) return
      this.downloadCanvas(this._lastOutCanvas, `roi_point_${Date.now()}.png`)
    },
    clearPreview() {
      this.previewUrl = null
      this._lastOutCanvas = null
    },
  },
}
</script>

<style>
.w-full { display: flex; flex-direction: column; align-items: center; justify-content: center; background-color: #f9f9f9; padding: 20px; }
.flex { display: flex; }
.flex-wrap { flex-wrap: wrap; }
.gap-6 { gap: 1.5rem; }
.relative { overflow: hidden; }
video { border-radius: 12px; box-shadow: 0 4px 6px rgba(0,0,0,0.1); background-color: black; display:block; }
canvas { position: absolute; top: 0; left: 0; border-radius: 12px; pointer-events: none; }
.text-lg { font-size: 1.125rem; font-weight: bold; }
.text-sm { font-size: 0.875rem; }
.list-disc { list-style-type: disc; }
.pl-5 { padding-left: 1.25rem; }
.leading-6 { line-height: 1.5rem; }
.bg-gray-200 { background-color: #e2e8f0; }
.bg-blue-600 { background-color: #2563eb; }
.text-white { color: white; }
.rounded-lg { border-radius: 0.5rem; }
.shadow { box-shadow: 0 4px 6px rgba(0,0,0,0.1); }
.hover\:bg-blue-700:hover { background-color: #1d4ed8; }
.text-gray-500 { color: #6b7280; }
.text-xs { font-size: 0.75rem; }
</style>
